import { useCallback } from 'react';
import { parseISO, differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { enUS, fr } from 'date-fns/locale';
import { useTranslation } from '../Translation';

/**
 * Custom hook for formatting dates with locale support and timezone handling
 * 
 * @returns {Object} Object containing formatting functions
 */
export const useDateFormat = () => {
    const { language } = useTranslation();
    
    // Get the appropriate locale based on the current language
    const getLocale = useCallback(() => {
        // Add more locales as needed
        switch (language) {
            case 'fr':
                return fr;
            case 'en':
            default:
                return enUS;
        }
    }, [language]);

    // Get the server timezone from environment variable or default to UTC
    const serverTimezone = process.env.REACT_APP_SERVER_TIMEZONE || 'UTC';
    
    // Get user's local timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Helper function to get days since Unix epoch (1970-01-01)
    const getDaysSinceEpoch = useCallback((date, timezone) => {
        const options = { timeZone: timezone };
        const year = parseInt(date.toLocaleString('en-US', { ...options, year: 'numeric' }));
        const month = parseInt(date.toLocaleString('en-US', { ...options, month: 'numeric' }));
        const day = parseInt(date.toLocaleString('en-US', { ...options, day: 'numeric' }));
        
        // Create a new date object in the specified timezone
        const tzDate = new Date(year, month - 1, day); // month is 0-indexed
        return Math.floor(tzDate.getTime() / (1000 * 60 * 60 * 24));
    }, []);
    
    // Format a date with the current locale and user's timezone
    // Timezone offset multiplier is used to adjust the date to the server timezone. 1 is the default which mean date is in server timezone and need to be adjusted to user timezone. -1 is the opposite.
    const formatDate = useCallback((date, formatStr = 'yyyy-MM-dd', timezoneOffsetMultiplier = 1) => {
        if (!date) return '';
        
        try {
            
            // Parse the date if it's a string
            const parsedDate = typeof date === 'string' ? parseISO(date) : date;
            
            // Get the date components in server timezone
            const serverDate = new Date(parsedDate);
            const serverOptions = { timeZone: serverTimezone };
            const serverHours = serverDate.toLocaleString('en-US', { ...serverOptions, hour: 'numeric', hour12: false });
            const serverMinutes = serverDate.toLocaleString('en-US', { ...serverOptions, minute: 'numeric' });
            
            // Get the date components in user timezone
            const userOptions = { timeZone: userTimezone };
            const userHours = serverDate.toLocaleString('en-US', { ...userOptions, hour: 'numeric', hour12: false });
            const userMinutes = serverDate.toLocaleString('en-US', { ...userOptions, minute: 'numeric' });

            // Get days since epoch for both timezones (more reliable than day of month)
            const serverDaysSinceEpoch = getDaysSinceEpoch(serverDate, serverTimezone);
            const userDaysSinceEpoch = getDaysSinceEpoch(serverDate, userTimezone);
            
            // Check if the days are different (crosses midnight boundary)
            const daysDifference = serverDaysSinceEpoch - userDaysSinceEpoch;
            
            // Calculate the offset in minutes
            const hoursDiff = (parseInt(serverHours) - parseInt(userHours)) + (daysDifference * 24);
            const minutesDiff = parseInt(serverMinutes) - parseInt(userMinutes);
            const totalMinutesDiff = (hoursDiff * 60) + minutesDiff;
            
            // Adjust the date
            const adjustedDate = new Date(parsedDate.getTime() - (totalMinutesDiff * 60000 * timezoneOffsetMultiplier));
            
            // Format the adjusted date
            return format(adjustedDate, formatStr, {
                locale: getLocale()
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return format(date, formatStr, { locale: getLocale() }); // Return formatted original date if conversion fails
        }
    }, [getLocale, serverTimezone, userTimezone, getDaysSinceEpoch]);
    
    // Get the difference in days with a human-readable format
    const getDaysSince = useCallback((date) => {
        if (!date) return '';
        
        const parsedDate = typeof date === 'string' ? parseISO(date) : date;
        return differenceInDays(new Date(), parsedDate);
    }, []);
    
    return {
        formatDate,
        getDaysSince,
        getLocale
    };
}; 